import { Slot } from "@radix-ui/react-slot";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/utils";
import type { ComponentProps } from "react";

export const buttonVariants = tv({
  base: "inline-flex gap-2 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold interactive has-icon has-icon-size-4 shrink-0 border cursor-pointer",
  variants: {
    variant: {
      default:
        "border-transparent bg-border-disabled text-foreground-secondary hover:bg-background-primary-disabled active:bg-background-pressed active:opacity-60",
      secondary:
        "border-transparent bg-foreground-secondary hover:opacity-80 active:opacity-60 text-border-disabled",
      outline:
        "border-border-pressed bg-background-neutral hover:bg-foreground-secondary active:bg-foreground-secondary active:opacity-60",
      pill: "border-border bg-background-neutral text-foreground-pressed hover:bg-foreground-secondary active:bg-foreground-secondary active:opacity-60",
      critical:
        "border-transparent bg-background-critical text-foreground-on-critical hover:bg-background-critical-hovered active:bg-background-critical-hovered active:opacity-60",
      success:
        "border-transparent bg-green-600 text-foreground hover:bg-green-700 active:bg-green-800",
      ghost:
        "border-transparent text-foreground-hovered hover:bg-background-neutral-hovered hover:text-foreground active:bg-transparent active:text-foreground active:opacity-60",
      link: "!p-0 !h-auto border-transparent text-blue-400 underline-offset-4 hover:underline hover:text-blue-500 active:text-blue-600",
    },
    size: {
      // Same icon size as sm (`has-icon-size-4`); tighter padding only.
      xs: "h-7 md:h-7 gap-1.5 py-0.5 px-2",
      sm: "h-8 md:h-9 py-1 px-3",
      md: "h-9 md:h-10 py-1 px-4",
      lg: "h-10 md:h-11 py-1 px-6",
      xl: "h-11 md:h-13 py-1 px-8",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "md",
  },
});

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export default function Button({
  size,
  asChild = false,
  variant,
  children,
  className,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function IconButton({
  size,
  asChild = false,
  variant,
  children,
  className,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ size, variant }),
        "px-0",
        {
          // xs keeps sm's icon size; only the hit box shrinks.
          "size-6 md:size-7": size === "xs",
          "size-8 md:size-9": size === "sm",
          "size-9 md:size-10": !size || size === "md",
          "size-10 md:size-11": size === "lg",
          "size-11 md:size-13": size === "xl",
        },
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}
